// Row helpers for the care plan editor (default.care.plan.settings.form.js).
//
// Questionnaires and exercises are edited as ORDERED ROWS, each with its own
// key, so one form or one exercise can appear in several windows: a
// questionnaire daily for two weeks and then every 5 days, an exercise
// pre-op and again from week 2 with different sets. The server merges
// questionnaire rows for the same form into one stored day list and splits
// it back into rows on read (physio_server business/careplanschedule.js);
// exercise rows map one-to-one onto regimen rows, which already allow
// repeats. Pure functions, no React, so they can be unit-tested.

export const DEFAULT_START = -30
export const DEFAULT_END = 90

// Named frequencies. Any other positive N is shown as "Every N days".
export const FREQUENCY_PRESETS = [
  { label: 'Daily', value: 1 },
  { label: 'Weekly', value: 7 },
  { label: 'Every 2 weeks', value: 14 },
  { label: 'Monthly', value: 30 },
]

export function frequencyLabel(n) {
  const v = Number(n)
  const preset = FREQUENCY_PRESETS.find((p) => p.value === v)
  if (preset) return preset.label
  if (!Number.isFinite(v) || v < 1) return 'Daily'
  return `Every ${v} days`
}

let seq = 0
// A fresh row key. Keys only have to be unique within a mounted editor.
export const nextKey = () => `row${++seq}`

const numOr = (v, d) =>
  v === null || v === undefined || isNaN(Number(v)) ? d : Number(v)

// Server items -> rows, in the order the server returned them (one row per
// uniform run for questionnaires, one per regimen row for exercises).
export function rowsFromItems(items, idField) {
  return (items || []).map((it) => ({
    key: nextKey(),
    ...it,
    [idField]: String(it[idField]),
    startdate: numOr(it.startdate, DEFAULT_START),
    enddate: numOr(it.enddate, DEFAULT_END),
  }))
}

// The distinct ids present, first-seen order: what the picker shows selected.
export function idsOf(rows, idField) {
  const out = []
  const seen = {}
  for (const r of rows || []) {
    const id = r[idField]
    if (!seen[id]) {
      seen[id] = true
      out.push(id)
    }
  }
  return out
}

export function patchRow(rows, key, patch) {
  return (rows || []).map((r) => (r.key === key ? { ...r, ...patch } : r))
}

export function removeRow(rows, key) {
  return (rows || []).filter((r) => r.key !== key)
}

// "Add another window" for the row with `key`: a copy inserted right after
// it, covering the same number of days starting the day after it ends. The
// clinician then edits the dates and frequency.
export function addWindowAfter(rows, key) {
  const idx = (rows || []).findIndex((r) => r.key === key)
  if (idx === -1) return rows
  const prev = rows[idx]
  const length = Math.max(0, Number(prev.enddate) - Number(prev.startdate))
  const startdate = Number(prev.enddate) + 1
  const copy = { ...prev, key: nextKey(), startdate, enddate: startdate + length }
  return rows.slice(0, idx + 1).concat([copy], rows.slice(idx + 1))
}

// How many rows share this row's id (drives the per-row remove button).
export function countOfId(rows, idField, id) {
  return (rows || []).filter((r) => r[idField] === id).length
}

// THE PICKER. Another window is added the same way the first one is: pick
// the item in the dropdown again. antd's multi-select would DE-select an
// option that is already selected, so the dropdown entries are never in the
// selected state: an option's value is the id plus PICK_SUFFIX, while the
// tags above the list (the Select's `value`, labelInValue) carry the plain
// id. Every click on an option therefore arrives as a new "#pick" entry; a ×
// on a tag arrives as that id missing from the values.
export const PICK_SUFFIX = '#pick'
export const pickValue = (id) => `${id}${PICK_SUFFIX}`

// The Select's value: one tag per distinct id, in row order.
export function tagValues(rows, idField, labelOf) {
  return idsOf(rows, idField).map((id) => ({ value: id, label: labelOf(id) }))
}

// Select onChange values (labelInValue objects or plain strings) ->
// { kept: tag ids still present, picked: ids clicked in the dropdown }.
export function splitPickerValues(values) {
  const kept = []
  const picked = []
  for (const v of values || []) {
    const raw = v && typeof v === 'object' ? v.value : v
    if (raw === null || raw === undefined) continue
    const s = String(raw)
    if (s.endsWith(PICK_SUFFIX)) picked.push(s.slice(0, -PICK_SUFFIX.length))
    else kept.push(s)
  }
  return { kept, picked }
}

// Apply a picker change to the rows: rows of an id whose tag was removed go;
// a picked id gets another window after its last row, or its first (default)
// row when it is new to the plan.
export function applyPickerChange(rows, values, idField, defaultFor) {
  const { kept, picked } = splitPickerValues(values)
  let next = (rows || []).filter(
    (r) => kept.indexOf(r[idField]) !== -1 || picked.indexOf(r[idField]) !== -1
  )
  picked.forEach((id) => {
    next = next.some((r) => r[idField] === id)
      ? addWindowForId(next, idField, id)
      : next.concat([{ key: nextKey(), [idField]: id, ...defaultFor(id) }])
  })
  return next
}

// Another window for `id`: a copy of its LAST row placed right after it (see
// addWindowAfter). An id with no rows is left alone — the plain option adds
// the first one.
export function addWindowForId(rows, idField, id) {
  let last = -1
  ;(rows || []).forEach((r, i) => {
    if (r[idField] === id) last = i
  })
  if (last === -1) return rows
  return addWindowAfter(rows, rows[last].key)
}

export function feedbackPayload(rows) {
  return (rows || []).map((r) => ({
    formid: r.formid,
    startdate: Number(r.startdate),
    enddate: Number(r.enddate),
    interval: Number(r.interval) >= 1 ? Math.trunc(Number(r.interval)) : 1,
    skiponsurgerydate: !!r.skiponsurgerydate,
  }))
}

// ---------------------------------------------------------------------------
// TracPatch reps come FROM the care plan (decision 2026-10-16). The TracPatch
// panels show, per week, the reps of the care plan row covering that week,
// read-only. Week vocabulary is the apps': Pre-Op is -1 (every pre-surgery
// day), week w covers days 7w..7w+6, capped at 13 (tracpatch_reps.js in the
// patient app, step_goals.js on the server).
export const TRACPATCH_WEEKS = [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]

// Per-week reps from the care plan rows of ONE exercise
// ([{ startdate, enddate, repeat }]). A post-op week takes the row covering
// its first day; Pre-Op takes the row covering day -1, else the pre-op row
// that ends latest. A week no row covers is null (the exercise is not
// scheduled then). Returns { [week]: reps | null }.
export function repsByWeekFromRows(rows, weeks) {
  const list = (rows || []).filter(
    (r) => r && Number.isFinite(Number(r.startdate)) && Number.isFinite(Number(r.enddate))
  )
  const covering = (day) =>
    list.find((r) => Number(r.startdate) <= day && day <= Number(r.enddate)) || null
  const repsOf = (r) => {
    if (!r) return null
    const n = parseInt(r.repeat, 10)
    return n > 0 ? n : null
  }
  const out = {}
  ;(weeks || TRACPATCH_WEEKS).forEach((w) => {
    let row
    if (w < 0) {
      row = covering(-1)
      if (!row) {
        const pre = list
          .filter((r) => Number(r.enddate) < 0)
          .sort((a, b) => Number(b.enddate) - Number(a.enddate))
        row = pre[0] || null
      }
    } else {
      row = covering(7 * w)
    }
    out[w] = repsOf(row)
  })
  return out
}

// Strip the rep fields from a TracPatch settings override map
// ({ [exerciseId]: overrides }) before it is saved: `requiredReps`, and
// `reps` on every rep_milestone_by_week row (a row left with no numeric field
// goes; an exercise left with no fields goes). Reps live in the care plan now,
// so stale values must not linger where the apps' fallback chain could still
// find them.
export function stripTracpatchReps(exercisesMap) {
  const out = {}
  Object.keys(exercisesMap || {}).forEach((id) => {
    const o = { ...(exercisesMap[id] || {}) }
    delete o.requiredReps
    if (Array.isArray(o.rep_milestone_by_week)) {
      const rows = o.rep_milestone_by_week
        .map((r) => {
          if (!r) return null
          const { reps, ...rest } = r
          const hasValue = Object.keys(rest).some(
            (k) => k !== 'week' && rest[k] !== undefined && rest[k] !== null
          )
          return hasValue ? rest : null
        })
        .filter(Boolean)
      if (rows.length) o.rep_milestone_by_week = rows
      else delete o.rep_milestone_by_week
    }
    if (Object.keys(o).length) out[id] = o
  })
  return out
}

// Hold in whole seconds as the editor shows and sends it: '' for none. Reads
// the stored value whatever its shape ('30', '30 seconds', 1, null) — the
// server stores a bare number string, which is what the patient app parses.
export function holdSeconds(v) {
  if (v === null || v === undefined || v === '') return ''
  const n = parseInt(String(v), 10)
  return Number.isFinite(n) && n > 0 ? String(n) : ''
}

export function exercisePayload(rows) {
  return (rows || []).map((r) => ({
    exerciseid: r.exerciseid,
    startdate: Number(r.startdate),
    enddate: Number(r.enddate),
    set: String(r.set || '1'),
    repeat: String(r.repeat || ''),
    hold: holdSeconds(r.hold) || null,
    interval: 1,
    skiponsurgerydate: false,
  }))
}

// ---- Overlap check ----------------------------------------------------------
// Two rows of the SAME item must never share a day: the patient scheduler
// would prescribe the exercise twice that day (or merge questionnaire
// windows with different frequencies into one ambiguous list). Day ranges
// are inclusive, so -30..0 and 0..10 overlap on day 0; -30..-1 and 0..10 do
// not. Rows with a blank start or end are skipped (they fail on their own).
const dayOrNull = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// -> [{ id, a, b }] for every pair of rows of one id whose ranges overlap.
export const overlappingWindows = (rows, idField) => {
  const byId = {}
  rows.forEach((r) => {
    const id = r[idField]
    if (!byId[id]) byId[id] = []
    byId[id].push(r)
  })
  const conflicts = []
  Object.keys(byId).forEach((id) => {
    const list = byId[id]
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]
        const b = list[j]
        const as = dayOrNull(a.startdate)
        const ae = dayOrNull(a.enddate)
        const bs = dayOrNull(b.startdate)
        const be = dayOrNull(b.enddate)
        if (as === null || ae === null || bs === null || be === null) continue
        if (as <= be && bs <= ae) conflicts.push({ id, a, b })
      }
    }
  })
  return conflicts
}

// Row keys involved in any conflict, for highlighting.
export const overlapKeys = (conflicts) => {
  const keys = new Set()
  conflicts.forEach((c) => {
    keys.add(c.a.key)
    keys.add(c.b.key)
  })
  return keys
}

// "Mini Squats: days -30 to 10 and days 5 to 20" — one line per conflict.
export const describeOverlap = (c, labelOf) =>
  `${labelOf(c.id)}: days ${c.a.startdate} to ${c.a.enddate} and days ${c.b.startdate} to ${c.b.enddate}`
